import { Link } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import LegalPageLayout, {
  LegalSection as H2,
  LegalParagraph as P,
} from '@components/legal/LegalPageLayout';

const LAST_UPDATED = '16 June 2026';

/**
 * Terms of Service (DRAFT). Public route /legal/terms. Content is a working
 * draft pending counsel review; the banner in LegalPageLayout states this.
 */
function TermsOfServicePage() {
  const { t } = useTranslation();

  return (
    <LegalPageLayout title={t('legal.terms')} lastUpdated={LAST_UPDATED}>
      <P>
        These Terms of Service (&ldquo;Terms&rdquo;) govern your access to and use of the
        accessibility-testing platform (the &ldquo;Service&rdquo;) operated by AFixt (the
        &ldquo;Company,&rdquo; &ldquo;we,&rdquo; &ldquo;us&rdquo;). By creating an account or using
        the Service, you agree to these Terms. If you are entering into these Terms on behalf of an
        organization, you represent that you have authority to bind that organization.
      </P>

      <H2>1. The Service</H2>
      <P>
        The Service combines automated accessibility scanning with human-validated manual testing,
        producing evidence-backed findings and conformance reporting (including VPAT/ACR exports).
        Automated scanning of your own content is available without charge; paid subscriptions add
        history, lifecycle tracking, reporting, and attestation features.
      </P>

      <H2>2. Accounts and eligibility</H2>
      <P>
        You must provide accurate account information and keep your credentials secure. You are
        responsible for activity under your account and for your team members&rsquo; use of the
        Service. You must be legally able to enter into these Terms.
      </P>

      <H2>3. Acceptable use</H2>
      <P>
        You agree not to: (a) test or scan properties you are not authorized to assess; (b)
        interfere with or disrupt the Service or its infrastructure; (c) attempt to access data
        belonging to other customers; (d) reverse engineer the Service except as permitted by law;
        or (e) use the Service to violate any applicable law or third-party right.
      </P>

      <H2>4. Customer data and ownership</H2>
      <P>
        You retain all rights to the content you submit, ingest, or generate through the Service
        (&ldquo;Customer Data&rdquo;), including scanned HTML, screenshots, findings, and
        attestation records. You grant us a limited license to process Customer Data solely to
        provide and improve the Service. Our handling of personal data is described in the{' '}
        <Link component={RouterLink} to="/legal/privacy">
          {t('legal.privacy')}
        </Link>{' '}
        and, where we act as a processor, the{' '}
        <Link component={RouterLink} to="/legal/dpa">
          {t('legal.dpa')}
        </Link>
        .
      </P>

      <H2>5. Fees and billing</H2>
      <P>
        Paid plans are billed in advance on a recurring basis according to the plan you select. Plan
        limits are enforced at registration of tracked Contexts; scanning and execution are never
        metered. Fees are non-refundable except where required by law. We may change pricing
        prospectively with notice; changes do not affect the current billing period.
      </P>

      <H2>6. Intellectual property</H2>
      <P>
        The Service, including its software, rule corpus, documentation, and brand, is owned by the
        Company and its licensors and is protected by intellectual-property laws. These Terms grant
        you a non-exclusive, non-transferable right to use the Service during your subscription.
      </P>

      <H2>7. Confidentiality</H2>
      <P>
        Each party may receive confidential information of the other. The receiving party will
        protect it with reasonable care and use it only to exercise rights and perform obligations
        under these Terms.
      </P>

      <H2>8. Warranties and disclaimers</H2>
      <P>
        The Service assists with accessibility testing but does not guarantee legal compliance with
        any standard or regulation. Conformance claims require a named human attestor; automated
        results alone are not a conformance determination. Except as expressly stated, the Service
        is provided &ldquo;as is&rdquo; without warranties of any kind to the maximum extent
        permitted by law.
      </P>

      <H2>9. Limitation of liability</H2>
      <P>
        To the maximum extent permitted by law, neither party is liable for indirect, incidental,
        special, or consequential damages, and each party&rsquo;s aggregate liability arising out of
        these Terms is limited to the amounts paid for the Service in the twelve months preceding
        the claim. Nothing limits liability that cannot be limited by law.
      </P>

      <H2>10. Term and termination</H2>
      <P>
        These Terms apply while you use the Service. You may cancel at any time; access continues
        through the end of the paid period. We may suspend or terminate access for material breach.
        On termination you may export your Customer Data for a reasonable period, after which it is
        deleted in accordance with our retention policies.
      </P>

      <H2>11. Changes to these Terms</H2>
      <P>
        We may update these Terms from time to time. Material changes will be communicated through
        the Service or by email. Continued use after changes take effect constitutes acceptance.
      </P>

      <H2>12. Governing law and contact</H2>
      <P>
        These Terms are governed by the laws of the jurisdiction in which the Company is established
        [jurisdiction to be specified by counsel]. Questions about these Terms can be sent to{' '}
        <Link href="mailto:legal@afixt.com">legal@afixt.com</Link>.
      </P>
    </LegalPageLayout>
  );
}

export default TermsOfServicePage;
